import { useFormik } from "formik";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import email from "../../../assests/email.svg";
import password from "../../../assests/password.svg";
import {
  isDefaultTippingViewDisabled,
  notifyBugsnagError,
  parseJwt,
  setLocalUserData,
  showToast,
} from "../../../common/utils/Functions";
import AuthLayout from "../../../components/layouts/AuthLayout";
import InputField from "../../../components/theme/InputField";
import ThemeButton from "../../../components/theme/ThemeButton";
import useFetch from "../../../hooks/useFetch";
import { setPropertiesCount, setUserData } from "../../../redux/common";
import { loginValSchema } from "../../../validations/AuthSchema";
import styles from "./login.module.scss";

const isTippingOff = (value: unknown) =>
  value === 0 || value === "0" || value === false;

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isAdmin = !window.location.href.includes("mydashboard");

  /** Api definition */
  const [loginApi, { response, loading, error }] = useFetch("/auth/login", {
    method: "post",
  });

  /** Handle api response */
  useEffect(() => {
    if (response) {
      /**Client want not to show this message */
      // showToast(response.message as string, "success");
      const data = parseJwt(response.data.token);
      setLocalUserData(response.data.token);
      dispatch(setUserData(data || null));
      const totalTips = response.data.totalTips ?? 0;
      const totalProperties = Number(response.data.totalProperties ?? 0);
      localStorage.setItem("total_tips", String(totalTips));
      localStorage.setItem("properties_count", String(totalProperties));
      dispatch(setPropertiesCount(totalProperties));
      if (data?.role_id === 2 && totalTips === 0) {
        navigate("/QR-Codes-&-links");
      } else {
        navigate("/dashboard");
      }
      const tippingView =
        response?.data?.tipping_view ?? data?.tipping_view;
      const isTippingDisabled =
        isDefaultTippingViewDisabled(tippingView) ||
        isTippingOff(response?.data?.is_tipping_enabled) ||
        isTippingOff(response?.data?.tip_status) ||
        isTippingOff(data?.is_tipping_enabled) ||
        isTippingOff(data?.tip_status);
      if (data?.role_id === 2 && response?.data?.is_first_time_login === 1) {
        if (totalProperties > 0 || isTippingDisabled) {
          localStorage.setItem("showRatingsPrompt", "true");
          localStorage.removeItem("showTippingModal");
        } else {
          localStorage.setItem("showTippingModal", "true");
          localStorage.removeItem("showRatingsPrompt");
        }
      } else {
        localStorage.removeItem("showTippingModal");
        localStorage.removeItem("showRatingsPrompt");
      }
    }
    if (error) {
      showToast(error.message as string, "error");
    }
  }, [response, error]);

  /** Formik definition */
  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: loginValSchema,
    onSubmit: (values) => {
      loginApi({
        ...values,
        role_id: isAdmin ? 1 : 2,
       // role_id: 2,
      }).catch((e: any) => {
        // 400/401 are expected auth validation failures (e.g. wrong email/password),
        // handled by toast via `error` state, so don't report them as crashes.
        const statusCode = e?.code ?? e?.status ?? e?.response?.status;
        if (statusCode === 400 || statusCode === 401) {
          return;
        }

        notifyBugsnagError(e?.message || "Login API failed", {
          api: "loginApi",
          statusCode,
        });
      });
    },
  });

  return (
    <AuthLayout>
      <div className={styles.login}>
        <div className={styles.login_ByGoogle}>
          <div className={styles.formTitle}>
            <h2 className="test">Login Your Account</h2>
          </div>
        </div>
        <form
          className={styles.formFields}
          onSubmit={formik.handleSubmit}
          autoComplete="off"
        >
          <InputField
            formik={formik}
            name="email"
            label="Email"
            placeholder="Enter Email"
            icon={email}
          />
          <InputField
            formik={formik}
            name="password"
            label="Password"
            placeholder="Enter Password"
            icon={password}
            type="password"
            onKeyDown={(e) => {
              if (e.key === " ") {
                e.preventDefault();
              }
            }}
          />

          <Link className={`linkText`} to="/forgot-password">
            Forgot Password?
          </Link>
          <div className={styles.btnWrap}>
            <ThemeButton
              loading={loading}
              disabled={loading}
              type="submit"
            >
              Login
            </ThemeButton>
          </div>
        </form>
      </div>
    </AuthLayout>
  );
};

export default Login;